import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, profile as profileApi } from '../api';
import TekLoader from '../components/TekLoader';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');

    if (!storedToken) {
      setLoading(false);
      return;
    }

    const cleanToken = storedToken.replace('Bearer ', '');
    setToken(cleanToken);

    // Always fetch fresh profile (permissions may have changed since last login)
    profileApi.getProfile()
      .then((res) => {
        const freshUser = res.data.user;
        if (freshUser) {
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        }
      })
      .catch(() => {
        // Token is invalid/expired — clear session
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (credentials) => {
    const response = await auth.login(credentials);

    const userData = response.data.data || response.data.user || response.data;
    const authToken = response.headers?.authorization || response.data?.token;

    if (authToken) {
      const cleanToken = authToken.replace('Bearer ', '');
      setToken(cleanToken);
      localStorage.setItem('auth_token', cleanToken);
    }

    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    return response;
  }, []);

  const register = useCallback(async (userData) => {
    return auth.register(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const needsProfileCompletion = useCallback(() => {
    return user && !user.profile_completed_at;
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      register,
      logout,
      loading,
      updateUser,
      needsProfileCompletion
    }}>
      {loading ? (
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: 'var(--paper)' }}
        >
          <TekLoader size={22} label="Loading…" />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
